import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

import { ReactComponent as AwardIcon } from "../../icons/award.svg";
import { ReactComponent as BellIcon } from "../../icons/bell.svg";
import { ReactComponent as BottomIcon } from "../../icons/bottom.svg";
import { ReactComponent as CartIcon } from "../../icons/cart.svg";
import { ReactComponent as CheckIcon } from "../../icons/check.svg";
import { ReactComponent as ClipboardIcon } from "../../icons/clipboard.svg";
import { ReactComponent as CloseIcon } from "../../icons/close.svg";
import { ReactComponent as EnvelopeIcon } from "../../icons/envelope.svg";
import { ReactComponent as EyeIcon } from "../../icons/eye.svg";
import { ReactComponent as FacebookIcon } from "../../icons/facebook.svg";
import { ReactComponent as FlagIcon } from "../../icons/flag.svg";
import { ReactComponent as HandsIcon } from "../../icons/hands.svg";
import { ReactComponent as LeftIcon } from "../../icons/left.svg";
import { ReactComponent as NewspaperIcon } from "../../icons/newspaper.svg";
import { ReactComponent as RightIcon } from "../../icons/right.svg";
import { ReactComponent as SignoutIcon } from "../../icons/signout.svg";
import { ReactComponent as SendIcon } from "../../icons/send.svg";
import { ReactComponent as StarIcon } from "../../icons/star.svg";
import { ReactComponent as SteamIcon } from "../../icons/steam.svg";
import { ReactComponent as TopIcon } from "../../icons/top.svg";
import { ReactComponent as TwitchIcon } from "../../icons/twitch.svg";
import { ReactComponent as TwitterIcon } from "../../icons/twitter.svg";
import { ReactComponent as UserIcon } from "../../icons/user.svg";
import { ReactComponent as InstagramIcon } from "../../icons/instagram.svg";
import { ReactComponent as YoutubeIcon } from "../../icons/youtube.svg";
import { ReactComponent as DiscordIcon } from "../../icons/discord.svg";
import { ReactComponent as LinkedInIcon } from "../../icons/linkedin.svg";
import { ReactComponent as VerifiedIcon } from "../../icons/verified-checkmark.svg";

import { colors } from "../../css/theme";

const ICON_SIZES = {
  default: {
    key: "default",
    value: "16px",
  },
  sm: {
    key: "sm",
    value: "12px",
  },
  md: {
    key: "md",
    value: "18px",
  },
  lg: {
    key: "lg",
    value: "24px",
  },
  xl: {
    key: "xl",
    value: "30px",
  },
  "2xl": {
    key: "2xl",
    value: "36px",
  },
  "3xl": {
    key: "3xl",
    value: "48px",
  },
  "4xl": {
    key: "4xl",
    value: "60px",
  },
  "5xl": {
    key: "4xl",
    value: "72px",
  },
  "6xl": {
    key: "4xl",
    value: "96px",
  },
};

const getIconSizeValue = (key) => {
  if (key instanceof Object && key.value) return key.value;
  return ICON_SIZES[key] ? ICON_SIZES[key].value : "";
};

const getFillColor = (color) => {
  if (colors[color]) return colors[color];
  return color;
};

const AppIcon = ({ size, color, children, className, ...restProps }) => {
  const actualSize = getIconSizeValue(size);
  const fill = getFillColor(color);

  switch (children) {
    case "award":
      return (
        <AwardIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "bell":
      return (
        <BellIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "bottom":
      return (
        <BottomIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "cart":
      return (
        <CartIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "check":
      return (
        <CheckIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "clipboard":
      return (
        <ClipboardIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "close":
      return (
        <CloseIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "envelope":
    case "email":
      return (
        <EnvelopeIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "eye":
      return (
        <EyeIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "facebook":
      return (
        <FacebookIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "flag":
      return (
        <FlagIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "hands":
      return (
        <HandsIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "left":
      return (
        <LeftIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "newspaper":
      return (
        <NewspaperIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "signout":
      return (
        <SignoutIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "right":
      return (
        <RightIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "send":
      return (
        <SendIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "star":
      return (
        <StarIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "steam":
      return (
        <SteamIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "top":
      return (
        <TopIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "twitch":
      return (
        <TwitchIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "twitter":
      return (
        <TwitterIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "instagram":
      return (
        <InstagramIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "youtube":
      return (
        <YoutubeIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "discord":
      return (
        <DiscordIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "linkedin":
      return (
        <LinkedInIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
    case "verified":
      return (
        <VerifiedIcon
          width={actualSize}
          height={actualSize}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      )
    case "user":
    default:
      return (
        <UserIcon
          width={actualSize}
          height={actualSize}
          fill={fill}
          className={classNames("inline", "leading-none", className)}
          {...restProps}
        />
      );
  }
};

AppIcon.propTypes = {
  children: PropTypes.string.isRequired,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  color: PropTypes.string,
  className: PropTypes.string,
};

AppIcon.defaultProps = {
  size: ICON_SIZES.default,
  color: colors.white,
  className: "",
};

export default AppIcon;
